import { ClientCreation, ClientCreationWithErrors, ClientRaw, ErrorClient } from '@Arti-zen/package-backoffice'
import React, { useCallback, useState } from 'react'
import { getCorrectedClient } from '../clients.helper'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import { Title } from './ArtisanUploadClients'
import ClientListTable from '../ClientListTable'
import { Button } from '@mui/material'
import FormClientError from '../errorListTable/FormClientError'
import SeeDuplicates from './SeeDuplicates'

type TProps = {
  clients: ClientCreation[]
  setClients: React.Dispatch<React.SetStateAction<ClientCreation[]>>
  clientsWithErrors: ClientCreationWithErrors[]
  setClientsWithErrors: React.Dispatch<React.SetStateAction<ClientCreationWithErrors[]>>
  onSubmit: () => Promise<void>
  onModify: boolean
  duplicates: ClientRaw[]
}

export default function TableClients({
  clients,
  clientsWithErrors,
  setClients,
  setClientsWithErrors,
  onModify,
  onSubmit,
  duplicates,
}: TProps) {
  const [openedClientForm, setOpenedClientForm] = useState<ClientCreation | ClientCreationWithErrors | null>(null)
  const [view, setView] = useState<'withErrors' | 'withoutErrors'>(
    clientsWithErrors?.length > 0 ? 'withErrors' : 'withoutErrors'
  )

  const replaceAllErrors = useCallback(
    ({
      property,
      condition,
      getCorrections,
    }: {
      property: keyof ClientCreation
      condition?: (client: ClientCreation) => boolean
      getCorrections: (client: ClientCreation, error: ErrorClient | null) => Partial<ClientCreation> | null
    }) => {
      const newClientsWithErrors = []
      const newClients = []

      for (let i = 0; i < clientsWithErrors.length; i++) {
        const client = clientsWithErrors[i]
        const error = client.errors[property]
        if (!error && (!!condition ? !condition(client) : true)) {
          newClientsWithErrors.push(client)
          continue
        }
        const corrections = getCorrections(client, error)

        const { correctedClient, remainingErrors } = getCorrectedClient(client, corrections)

        Object.keys(remainingErrors).length === 0
          ? newClients.push(correctedClient)
          : newClientsWithErrors.push({ ...correctedClient, errors: remainingErrors })
      }

      setClients((prev) => [...prev, ...newClients])
      setClientsWithErrors(newClientsWithErrors)

      if (newClientsWithErrors.length === 0) {
        setView('withoutErrors')
      }
    },
    [clientsWithErrors]
  )

  const correctClient = ({
    corrections,
    client,
  }: {
    corrections: Partial<ClientCreationWithErrors>
    client: ClientCreationWithErrors
  }) => {
    const { correctedClient, remainingErrors } = getCorrectedClient(client, corrections)

    if (!client.errors) {
      setClients((previous) => {
        const newClients = [...previous]
        const indexOf = onModify ? newClients.findIndex((c) => c.id === client.id) : client.id
        newClients[indexOf] = correctedClient
        return newClients
      })
    }

    if (Object.keys(remainingErrors)?.length === 0) {
      setClients((previous) => [...previous, correctedClient])

      setClientsWithErrors((previous) => {
        const newClientsWithErrors = [...previous]
        const indexOf = onModify ? newClientsWithErrors.findIndex((c) => c.id === client.id) : client.id
        newClientsWithErrors.splice(indexOf, 1)
        if (newClientsWithErrors.length === 0) setView('withoutErrors')
        return newClientsWithErrors
      })
      return
    }

    setClientsWithErrors((previous) => {
      const newClientsWithErrors = [...previous]
      const indexOf = onModify ? newClientsWithErrors.findIndex((c) => c.id === client.id) : client.id
      newClientsWithErrors[indexOf] = { ...correctedClient, errors: remainingErrors }
      return newClientsWithErrors
    })
  }

  return (
    <>
      <SeeDuplicates duplicates={duplicates} onModify={onModify} />

      <Row style={{ justifyContent: 'flex-start' }}>
        <Tab selected={view === 'withErrors'} onClick={() => setView('withErrors')}>
          Clients avec erreurs ({clientsWithErrors?.length ?? 0})
        </Tab>
        <Tab selected={view === 'withoutErrors'} onClick={() => setView('withoutErrors')}>
          Clients sans erreurs ({clients?.length ?? 0})
        </Tab>
      </Row>
      {view === 'withErrors' && (
        <>
          <Title>Clients avec erreurs : {clientsWithErrors?.length}</Title>
          <ClientListTable
            data={clientsWithErrors}
            correctClient={correctClient}
            openForm={(client, index) => {
              setOpenedClientForm(client)
            }}
            onModify={onModify}
            replaceAllErrors={replaceAllErrors}
          />
        </>
      )}
      {view === 'withoutErrors' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: 1,
          }}
        >
          <>
            <Title>Clients à importer : {clients?.length ?? '0'}</Title>
            <ClientListTable
              data={clients}
              correctClient={correctClient}
              openForm={(client, index) => {
                setOpenedClientForm(client)
              }}
              onModify={onModify}
              replaceAllErrors={null}
            />
          </>
          <Button
            onClick={onSubmit}
            style={{
              display: 'flex',
              alignSelf: 'center',
              backgroundColor:
                clientsWithErrors?.length > 0 || clients?.length === 0
                  ? Theme.colors.greyLight
                  : Theme.colors.blueArtizen,
              color: 'white',
              width: '100%',
              margin: '30px 0',
            }}
            disabled={clientsWithErrors?.length > 0 || clients?.length === 0}
          >
            {onModify ? 'Modifier' : 'Importer'} {clients?.length ?? ''} client{clients?.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}
      <FormClientError
        client={openedClientForm}
        onClose={() => {
          setOpenedClientForm(null)
        }}
        correctClient={(corrections) =>
          correctClient({ corrections, client: openedClientForm as ClientCreationWithErrors })
        }
      />
    </>
  )
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
`

const Tab = styled.div<{ selected: boolean }>`
  border: 1px solid ${Theme.colors.greenArtizen};
  background-color: ${({ selected }) => (selected ? Theme.colors.greenArtizen : 'white')};
  color: ${({ selected }) => (selected ? 'white' : Theme.colors.greenArtizen)};
  padding: 15px;
  border-radius: 5px;
  font-size: 13px;
  &:hover {
    cursor: pointer;
    background-color: ${({ selected }) => `#1edb97${selected ? '90' : '10'}`};
  }
`
